import { useState } from "react";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";
import { Box, Button, Link, Typography } from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../app/appHooks";
import {
  accountCreationInit,
  goToAccountCreation,
  goToEmailVerification,
  goToInstructions,
  setAccountType,
} from "../loginSlice";
import { AccountType, Views } from "../../../helpers/constants";
import ArrowBackIosIcon from "@material-ui/icons/ArrowBackIos";
import { ActionBarBottom } from "./ActionBarBottom";
import { StackLogo } from "../StackLogo";
import { ConfirmDialog, DialogTypes } from "../../common/ConfirmDialog";

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {
      width: "380px",
      marginTop: theme.spacing(4),
      display: "flex",
      alignItems: "center",
      flexDirection: "column",
      gap: theme.spacing(3),
      height: "65%",
      [theme.breakpoints.down("sm")]: {
        width: "360px",
      },
      [theme.breakpoints.down("xs")]: {
        width: "100%",
      },
    },
    title: {
      fontSize: theme.typography.h6.fontSize,
      fontFamily: theme.typography.secondaryFontFamily,
      textAlign: "center",
    },
    optionBox: {
      width: "100%",
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: "8px",
      padding: theme.spacing(3),
      cursor: "pointer",
      transition: "border-color 0.2s",
      "&:hover": {
        borderColor: theme.palette.primary.main,
      },
    },
    optionBoxSelected: {
      border: `2px solid ${theme.palette.primary.main}`,
    },
    optionTitle: {
      fontFamily: theme.typography.secondaryFontFamily,
      fontSize: theme.typography.body1.fontSize,
      marginBottom: theme.spacing(0.5),
    },
    optionDescription: {
      fontSize: theme.typography.body2.fontSize,
      color: theme.palette.text.secondary,
    },
    nextButton: {
      padding: theme.spacing(2, 8),
      maxHeight: 50,
    },
    buttonLabel: {
      fontSize: theme.typography.h6.fontSize,
    },
    link: {
      cursor: "pointer",
      fontFamily: theme.typography.secondaryFontFamily,
      fontWeight: 500,
      fontSize: theme.typography.subtitle1.fontSize,
      textAlign: "right",
    },
    secondaryButtons: {
      color: theme.palette.paused.light,
    },
  })
);

export const AccountTypeSelection = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const continueAccountCreation = useAppSelector(
    (state) => state.loginState.continueAccountCreation
  );

  const [selected, setSelected] = useState<AccountType | undefined>(undefined);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSelect = (type: AccountType) => {
    setSelected(type);
  };

  const handleNext = () => {
    if (!selected) return;
    if (selected === AccountType.Personal) {
      setShowConfirm(true);
    } else {
      dispatch(setAccountType(selected));
      if (continueAccountCreation) {
        dispatch(goToEmailVerification());
      } else {
        dispatch(goToInstructions());
      }
    }
  };

  const handleConfirmPersonal = () => {
    dispatch(setAccountType(AccountType.Personal));
    if (continueAccountCreation) {
      dispatch(goToEmailVerification());
    } else {
      dispatch(goToAccountCreation());
    }
  };

  const handleBackToLogin = () => {
    dispatch(accountCreationInit());
  };

  return (
    <>
      <ConfirmDialog
        dialogType={DialogTypes.primary}
        toggle={showConfirm}
        title={t("personal_account")}
        message={t("confirm_personal_account_type")}
        possitiveText={t("yes")}
        negativeText={t("cancel")}
        positiveAction={handleConfirmPersonal}
        negativeAction={() => void 0}
        onClose={() => setShowConfirm(false)}
      />
      <StackLogo />
      <div className={classes.container}>
        <Typography className={classes.title}>
          {t("choose_account_type")}
        </Typography>

        <Box
          className={`${classes.optionBox} ${
            selected === AccountType.Professional
              ? classes.optionBoxSelected
              : ""
          }`}
          onClick={() => handleSelect(AccountType.Professional)}
        >
          <Typography className={classes.optionTitle}>
            {t("professional_business")}
          </Typography>
          <Typography className={classes.optionDescription}>
            {t("professional_business_description")}
          </Typography>
        </Box>

        <Box
          className={`${classes.optionBox} ${
            selected === AccountType.Personal ? classes.optionBoxSelected : ""
          }`}
          onClick={() => handleSelect(AccountType.Personal)}
        >
          <Typography className={classes.optionTitle}>
            {t("personal_account")}
          </Typography>
          <Typography className={classes.optionDescription}>
            {t("personal_account_description")}
          </Typography>
        </Box>

        <Button
          variant={selected ? "contained" : "outlined"}
          color="primary"
          size="large"
          className={classes.nextButton}
          classes={{ label: classes.buttonLabel }}
          disabled={!selected}
          onClick={handleNext}
        >
          {t("next")}
        </Button>
      </div>
      <ActionBarBottom>
        <Link
          className={classes.link}
          onClick={handleBackToLogin}
          to={Views.LOGIN}
          component={RouterLink}
          underline="none"
        >
          <Button
            className={classes.secondaryButtons}
            startIcon={<ArrowBackIosIcon />}
          >
            {t("action_back")}
          </Button>
        </Link>
      </ActionBarBottom>
    </>
  );
};
